import { jsx as _jsx, jsxs as _jsxs } from "react/jsx-runtime";
import { useMemo } from 'react';
import { Button, Typography } from 'seagull';
import { AnchorLogo } from 'seagull/icons/color';
import { InvoicePreviewSkeleton } from '../NotificationSkeletons/InvoicePreviewSkeleton';
import { PayoutPreviewSkeleton } from '../NotificationSkeletons/PayoutPreviewSkeleton';
import styles from './NotificationEmailPreview.module.scss';
import { GetNotificationEmailPreviewTID } from './NotificationEmailPreview.testIds';
import { pickSkeleton, resolveSegments, useNotificationParagraphs, useNotificationPreviewContext } from './NotificationEmailPreview.utils';
import { SegmentedText } from './SegmentedText';
export function NotificationEmailPreview(param) {
    let { notificationName, subject, message, templateVariables, customTemplateVariables, ctaButtonText, branding, vendorBusinessName, currentDate, testId = 'notification-email-preview' } = param;
    const { NotificationEmailPreviewTestIds: testIds } = useMemo(()=>GetNotificationEmailPreviewTID(testId), [
        testId
    ]);
    const previewContext = useNotificationPreviewContext(vendorBusinessName, currentDate);
    const subjectSegments = useMemo(()=>resolveSegments(subject, templateVariables, customTemplateVariables, previewContext), [
        subject,
        templateVariables,
        customTemplateVariables,
        previewContext
    ]);
    const paragraphs = useNotificationParagraphs(message, templateVariables, customTemplateVariables, previewContext);
    const skeleton = pickSkeleton(notificationName);
    return /*#__PURE__*/ _jsxs("div", {
        className: styles.root,
        "data-testid": testIds.Root(),
        children: [
            /*#__PURE__*/ _jsx(Typography, {
                as: "div",
                variant: "paragraph3",
                weight: "normal",
                className: styles.subjectBar,
                testId: testIds.Subject(),
                children: /*#__PURE__*/ _jsx(SegmentedText, {
                    segments: subjectSegments,
                    unresolvedTestId: testIds.UnresolvedTag
                })
            }),
            /*#__PURE__*/ _jsxs("div", {
                className: styles.emailCard,
                children: [
                    branding.type === 'anchor' ? /*#__PURE__*/ _jsx(AnchorLogo, {
                        className: styles.logo,
                        "aria-hidden": true,
                        "data-testid": testIds.AnchorLogo()
                    }) : branding.logoUrl != null ? /*#__PURE__*/ _jsx("img", {
                        src: branding.logoUrl,
                        alt: "",
                        className: styles.logo,
                        "data-testid": testIds.SenderLogo()
                    }) : /*#__PURE__*/ _jsx(Typography, {
                        variant: "h6",
                        weight: "bold",
                        className: styles.senderName,
                        testId: testIds.SenderName(),
                        children: branding.name
                    }),
                    /*#__PURE__*/ _jsxs("div", {
                        className: styles.body,
                        "data-testid": testIds.Body(),
                        children: [
                            paragraphs.map((segments, index)=>/*#__PURE__*/ _jsx(Typography, {
                                    as: "p",
                                    variant: "paragraph2",
                                    weight: "light",
                                    className: styles.paragraph,
                                    testId: testIds.Paragraph(index),
                                    children: /*#__PURE__*/ _jsx(SegmentedText, {
                                        segments: segments,
                                        unresolvedTestId: testIds.UnresolvedTag
                                    })
                                }, index)),
                            skeleton === 'invoice' && /*#__PURE__*/ _jsx(InvoicePreviewSkeleton, {
                                ctaButtonText: ctaButtonText,
                                testId: testIds.SkeletonInvoice()
                            }),
                            skeleton === 'payout' && /*#__PURE__*/ _jsx(PayoutPreviewSkeleton, {
                                ctaButtonText: ctaButtonText,
                                testId: testIds.SkeletonPayout()
                            })
                        ]
                    }),
                    skeleton == null && ctaButtonText !== '' && /*#__PURE__*/ _jsx("div", {
                        className: styles.ctaWrapper,
                        children: /*#__PURE__*/ _jsx(Button, {
                            className: styles.cta,
                            size: "small",
                            skin: "primary",
                            disabled: true,
                            bi: {
                                name: 'notification_email_preview_cta'
                            },
                            testId: testIds.CtaButton(),
                            children: ctaButtonText
                        })
                    })
                ]
            })
        ]
    });
}
